import { Box, Button, Flex, Grid, Group, Stack, Text, Title, UnstyledButton } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { get, isEqual } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo } from 'react';

import { Declaration, EORIGIN_DATE_FORMAT, ExitProofLogs } from '@e-origin/shared';

import { deleteIcon } from '@assets';
import { DatePickerInput, EditArray, MultiLangCodes, MultiSelect, TextInput } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import {
  buildTransportDocumentPointerOptions,
  createEmptySphItem,
  getProofOfExitRules,
  normalizeProofOfExitValues,
  shouldSphRequireTransportDocuments,
} from './ProofOfExitForm.rules';

export const ProofOfExitFields: React.FC<{ form: UseFormReturnType<ExitProofLogs>; declaration?: Declaration; disabled?: boolean; resetKey?: number }> = ({
  form,
  declaration,
  disabled = false,
  resetKey = 0,
}) => {
  const codelistSystems = useCodeListSystem({
    group: declaration?.generalInfo?.group,
    isExport: declaration?.generalInfo?.isExport,
  });
  const rules = useMemo(() => getProofOfExitRules(form.values, declaration), [declaration, form.values]);
  const pointerOptions = useMemo(() => buildTransportDocumentPointerOptions(form.values.transportDocuments), [form.values.transportDocuments]);

  useEffect(() => {
    if (!declaration) {
      return;
    }

    const normalizedValues = normalizeProofOfExitValues(form.values, declaration);

    if (!isEqual(normalizedValues, form.values)) {
      form.setValues(normalizedValues);
    }
  }, [declaration, form, form.values]);

  const sphItems = rules.sphItems;

  const setSphItems = (nextSphItems: typeof sphItems) => {
    form.setFieldValue('additionalInformation', [rules.aazItem, ...nextSphItems]);
  };

  const updateSphItem = (index: number, nextItem: (typeof sphItems)[number]) => {
    const nextSphItems = [...sphItems];
    nextSphItems[index] = nextItem;
    setSphItems(nextSphItems);
  };

  const removeSphItem = (index: number) => {
    setSphItems(sphItems.filter((_, currentIndex) => currentIndex !== index));
  };

  const onChangeAdditionalInformationSph = (value) =>
    form.setFieldValue('additionalInformation', [
      {
        ...rules.aazItem,
        type: 'AAZ',
        code: value,
      },
      ...sphItems,
    ]);

  return (
    <>
      <Grid w="100%">
        <Grid.Col span={4}>
          <Text fw={600} mb="sm">
            Additional information
          </Text>
          <Stack gap="xs">
            <Group w="100%" grow>
              <TextInput label="Statement type" value={rules.aazItem.type} disabled data-pw="proof-of-exit-additional-information-type-0" />
              <MultiLangCodes
                key={`proof-of-exit-aaz-code-${resetKey}`}
                label="Statement code"
                value={rules.aazItem.code}
                onChange={onChangeAdditionalInformationSph}
                code="EnquiryInformation"
                includeList={rules.allowedAazCodes}
                disabled={disabled}
                required
                error={get(form.errors, 'additionalInformation.0.code')}
                system={codelistSystems}
                data-pw="proof-of-exit-additional-information-code-0"
              />
            </Group>

            <Text fw={400}>Alternative proof</Text>

            {sphItems.map((item, index) => {
              const additionalInformationIndex = index + 1;
              const requiresPointer = shouldSphRequireTransportDocuments(item.code);
              const showDeleteButton = !disabled;

              return (
                <Box key={`proof-of-exit-sph-${index}`} mb="xs">
                  <Flex justify="flex-start" align="center" data-pw={`proof-of-exit-additional-information-${additionalInformationIndex}`}>
                    <Stack gap="xs">
                      <Group align="top" w="100%" grow>
                        <TextInput
                          label={`Statement type ${additionalInformationIndex + 1}`}
                          value={item.type}
                          disabled
                          data-pw={`proof-of-exit-additional-information-type-${additionalInformationIndex}`}
                        />

                        <MultiLangCodes
                          label={`Statement code ${additionalInformationIndex + 1}`}
                          value={item.code}
                          onChange={(value) =>
                            updateSphItem(index, {
                              ...item,
                              code: value,
                              relation: shouldSphRequireTransportDocuments(value) ? item.relation : { pointers: [] },
                            })
                          }
                          code="TypeOfAlternativeEvidence"
                          disabled={disabled}
                          error={get(form.errors, `additionalInformation.${additionalInformationIndex}.code`)}
                          system={codelistSystems}
                          data-pw={`proof-of-exit-additional-information-code-${additionalInformationIndex}`}
                        />
                      </Group>

                      {requiresPointer && (
                        <MultiSelect
                          label="Related transport documents"
                          data={pointerOptions}
                          value={(item.relation?.pointers ?? []).map((pointer) => `${pointer}`)}
                          onChange={(value) =>
                            updateSphItem(index, {
                              ...item,
                              relation: { pointers: value.map(Number) },
                            })
                          }
                          disabled={disabled || !rules.transportDocumentsRequired}
                          required
                          error={get(form.errors, `additionalInformation.${additionalInformationIndex}.relation.pointers`)}
                          data-pw={`proof-of-exit-additional-information-pointer-${additionalInformationIndex}`}
                        />
                      )}
                    </Stack>

                    {showDeleteButton && (
                      <UnstyledButton
                        ml="md"
                        onClick={(event) => {
                          event.preventDefault();
                          removeSphItem(index);
                        }}
                      >
                        <img src={deleteIcon} alt="Delete" />
                      </UnstyledButton>
                    )}
                  </Flex>
                </Box>
              );
            })}

            {!disabled && (
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => {
                  if (!rules.canAddSph) {
                    return;
                  }

                  setSphItems([...sphItems, createEmptySphItem()]);
                }}
                disabled={!rules.canAddSph}
                data-pw="add-proof-of-exit-additional-information-btn"
              >
                Add additional information
              </Button>
            )}

            {get(form.errors, 'additionalInformation') && (
              <Text c="red" fz={12}>
                {String(get(form.errors, 'additionalInformation'))}
              </Text>
            )}
          </Stack>
        </Grid.Col>
        {rules.transportDocumentsRequired && (
          <Grid.Col span={4}>
            <Title order={6} fw={600} mb="sm">
              Transport documents
            </Title>
            <EditArray
              addButtonLabel="Add transport document"
              addButtonDataPW="add-proof-of-exit-transport-document-btn"
              form={form}
              inputs={[
                {
                  type: 'MultiLang' as const,
                  code: 'CL754',
                  label: 'Type',
                  name: 'type',
                  width: 30,
                  'data-pw': 'proof-of-exit-transport-document-type',
                },
                {
                  type: 'Input' as const,
                  label: 'Reference number',
                  name: 'referenceNumber',
                  width: 40,
                  'data-pw': 'proof-of-exit-transport-document-reference-number',
                },
              ]}
              arrayPath="transportDocuments"
              codelistSystemsConfig={{}}
              disabled={disabled}
            />
            {get(form.errors, 'transportDocuments') && (
              <Text c="red" fz={12}>
                {String(get(form.errors, 'transportDocuments'))}
              </Text>
            )}
          </Grid.Col>
        )}
      </Grid>
      <Grid w="100%">
        <Grid.Col span={2}>
          <MultiLangCodes
            key={`proof-of-exit-exit-office-${resetKey}`}
            {...form.getInputProps('exitOffice')}
            label="Exit office"
            code="CustomsOfficeExit"
            disabled={disabled || !rules.enableExitOffice}
            required={rules.requiresExitOffice}
            error={get(form.errors, 'exitOffice')}
            system={codelistSystems}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <DatePickerInput
            label="Exit date"
            valueFormat={EORIGIN_DATE_FORMAT}
            disabled={disabled || !rules.exitDateRelevant}
            required={rules.exitDateRelevant}
            {...form.getInputProps('exitDate')}
            value={form.values.exitDate ? moment(form.values.exitDate).toDate() : null}
            onChange={(value: Date) => form.setFieldValue('exitDate', value ?? null)}
            minDate={rules.aazCode === '2' ? moment().add(1, 'day').startOf('day').toDate() : undefined}
            maxDate={rules.aazCode === '3' || rules.aazCode === '4' ? moment().endOf('day').toDate() : undefined}
            error={get(form.errors, 'exitDate')}
            clearable
          />
        </Grid.Col>
      </Grid>

      <Title order={6} fw={600}>
        Carrier
      </Title>
      <Grid w="100%">
        <Grid.Col span={2}>
          <TextInput
            {...form.getInputProps('carrier.identificationNumber')}
            label="Identification number"
            disabled={disabled || !rules.enableCarrier || rules.disableCarrierIdentificationNumber}
            error={get(form.errors, 'carrier') || get(form.errors, 'carrier.identificationNumber')}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <TextInput
            {...form.getInputProps('carrier.name')}
            label="Name"
            disabled={disabled || !rules.enableCarrier || rules.disableCarrierNameAndAddress}
            error={get(form.errors, 'carrier') || get(form.errors, 'carrier.name')}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <TextInput
            {...form.getInputProps('carrier.address.country')}
            label="Country"
            disabled={disabled || !rules.enableCarrier || rules.disableCarrierNameAndAddress}
            error={get(form.errors, 'carrier') || get(form.errors, 'carrier.address.country')}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <TextInput
            {...form.getInputProps('carrier.address.city')}
            label="City"
            disabled={disabled || !rules.enableCarrier || rules.disableCarrierNameAndAddress}
            error={get(form.errors, 'carrier') || get(form.errors, 'carrier.address.city')}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <TextInput
            {...form.getInputProps('carrier.address.street')}
            label="Street"
            disabled={disabled || !rules.enableCarrier || rules.disableCarrierNameAndAddress}
            error={get(form.errors, 'carrier') || get(form.errors, 'carrier.address.street')}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <TextInput
            {...form.getInputProps('carrier.address.postCode')}
            label="Postcode"
            disabled={disabled || !rules.enableCarrier || rules.disableCarrierNameAndAddress}
            error={get(form.errors, 'carrier') || get(form.errors, 'carrier.address.postCode')}
          />
        </Grid.Col>
      </Grid>
    </>
  );
};
